import type {HttpHandler, WebHandler} from './types';
import {DefaultWebExchange, ServerHttpResponseDecorator} from './exchange.js';
import {AsyncLocalStorage} from 'node:async_hooks';
import type { Logger } from '@interopio/gateway/logging/api';
import {HttpHeaders} from '../../types/web/http';
import {ServerHttpRequest, ServerHttpResponse, ServerWebExchange} from '../../types/web/server';
import getLogger from '../logger.js';
import {HttpStatus} from "../http/status.ts";

export class HttpHeadResponseDecorator extends ServerHttpResponseDecorator {

}

class HandlerAdapter {

    readonly #logger: Logger
    #enableLoggingRequestDetails: boolean = false;
    readonly #delegate: WebHandler;
    #storage?: AsyncLocalStorage<{ exchange: ServerWebExchange }>;

    constructor(logger: Logger, delegate: WebHandler) {
        this.#logger = logger;
        this.#delegate = delegate;
    }

    protected createExchange(request: ServerHttpRequest, response: ServerHttpResponse): ServerWebExchange {

        const exchange = new DefaultWebExchange(request, response);
        return exchange;
    }

    set storage(storage: AsyncLocalStorage<{ exchange: ServerWebExchange }>) {
        this.#storage = storage;
    }

    set enableLoggingRequestDetails(value: boolean) {
        this.#enableLoggingRequestDetails = value;
    }

    formatHeaders(headers: HttpHeaders): string {
        let result = '{';

        for (const key of headers.keys()) {
            if (!this.#enableLoggingRequestDetails) {
                result += 'masked, ';
                break;
            } else {
                const value = headers.get(key);
                result += `"${key}": "${value}", `;
            }
        }
        if (result.endsWith(', ')) {
            result = result.slice(0, -2);
        }
        result += '}';
        return result;

    }

    formatRequest(request: ServerHttpRequest): string {
        const query = request.URL.search;
        return `HTTP ${request.method} "${request.path}${query}`;
    }

    logRequest(exchange: ServerWebExchange) {
        if (this.#logger.enabledFor('debug')) {
            const trace = this.#logger.enabledFor('trace');
            this.#logger.debug(`${exchange.logPrefix}${this.formatRequest(exchange.request)}${trace ? `, headers: ${this.formatHeaders(exchange.request.headers)}` : ''}"`);

        }
    }

    logResponse(exchange: ServerWebExchange) {
        if (this.#logger.enabledFor('debug')) {
            const trace = this.#logger.enabledFor('trace');
            const status = exchange.response.statusCode;
            this.#logger.debug(`${exchange.logPrefix}Completed ${status ?? '200 OK'}${trace ? `, headers: ${this.formatHeaders(exchange.response.headers)}` : ''}"`);
        }
    }

    handleUnresolvedError(exchange: ServerWebExchange, error: Error) {
        const {request, response, logPrefix} = exchange;

        if (response.setStatusCode(HttpStatus.INTERNAL_SERVER_ERROR)) {
            this.#logger.error(`${logPrefix}500 Server Error for ${this.formatRequest(request)}`, error);
            return;
        }
        // todo if client disconnected, we should not log an error

        this.#logger.error(`${logPrefix}Error [${error.message} for ${this.formatRequest(request)}, but already ended (${response.statusCode})`, error);
        throw error;

    }

    async web(exchange: ServerWebExchange): Promise<void> {
        return await this.#delegate(exchange);
    }

    async http(request: ServerHttpRequest, response: ServerHttpResponse): Promise<void> {

        const exchange = this.createExchange(request, response);
        const callback = () => {
            this.logRequest(exchange);
            return this.web(exchange)
                .then(() => {
                    this.logResponse(exchange);
                })
                .catch((error: Error) => {
                    this.handleUnresolvedError(exchange, error)
                })
                .then(async () => {
                    await exchange.response.end();
                });
        };

        await new Promise<void>((resolve, reject) => {
            if (this.#storage !== undefined) {
                this.#storage.run({exchange}, () => {
                    callback().then(() => resolve()).catch((error) => reject(error));
                });
            } else {
                callback().then(() => resolve()).catch((error) => reject(error));
            }
        });
    }
}

export class WebHttpHandlerBuilder {
    readonly #webHandler: WebHandler;
    #storage?: AsyncLocalStorage<{ exchange: ServerWebExchange }> = new AsyncLocalStorage();
    #handlerDecorator?: (handler: HttpHandler) => HttpHandler;

    storage(storage: AsyncLocalStorage<{ exchange: ServerWebExchange }>): this {
        this.#storage = storage;
        return this;
    }

    httpHandlerDecorator(decorator: (handler: HttpHandler) => HttpHandler): this {
        if (this.#handlerDecorator === undefined) {
            this.#handlerDecorator = decorator;
        } else {
            const previousDecorator = this.#handlerDecorator;
            this.#handlerDecorator = (handler: HttpHandler) => {
                handler = previousDecorator(handler);
                return decorator(handler);
            }
        }
        return this;
    }

    constructor(webHandler: WebHandler) {
        this.#webHandler = webHandler;
    }

    build(): HttpHandler {
        const logger = getLogger('http');

        const adapter = new HandlerAdapter(logger, this.#webHandler);
        if (this.#storage !== undefined) adapter.storage = this.#storage;
        adapter.enableLoggingRequestDetails = false;
        const adapted: HttpHandler = async (request, response) => adapter.http(request, response);


        return this.#handlerDecorator ? this.#handlerDecorator(adapted) : adapted;
    }
}
